import { clone, createId } from "../scene/ops";
import {
  MAX_TRACKS,
  type OverlayClip,
  type SceneDocument,
  type SceneElement,
  type Timeline,
  type TrackGroup,
  type TrackInfo,
} from "../scene/schema";

/**
 * Tracks of video documents (docs/05-video-und-untertitel.md), like in
 * Premiere or Final Cut: the main track (0) at the bottom, above it tracks
 * 1 … n. A track holds clips (B-roll, picture in picture) and elements
 * (titles, logos, captions) that never overlap in time; higher tracks lie on
 * top. So a title can lie between the speaker and a B-roll above it.
 *
 * - Clips keep their `track` (OverlayClip), top-level elements get `track`
 *   too. Elements without one (older documents, freshly inserted ones) lie
 *   above everything they overlap – `resolveElementTracks` computes that, and
 *   `normalizeVideoLayers` writes it into the document on the next change.
 * - `timeline.tracks[i]` describes track `i + 1`: name, hidden, kept when
 *   empty, group. Tracks without content disappear unless they are kept.
 * - Groups (`timeline.trackGroups`) are consecutive tracks that fold into one
 *   row and move together.
 * - The cut-out person (video/cutout.ts) has a track of its own, the
 *   person track (`kind: "person"`): it holds nothing, the persons of the
 *   lowest cut-out track are drawn there. Everything on the tracks below it
 *   – titles, pictures, videos – lies behind the person; it moves like any
 *   track. Older documents marked elements with `behindCutout` instead;
 *   `normalizeVideoLayers` turns them into a track below the person.
 */

/** Track number of the main track. */
export const MAIN_TRACK_NUMBER = 0;

/** Something on a track: a clip of an upper track or a top-level element. */
export interface TrackEntry {
  kind: "clip" | "element";
  id: string;
  track: number;
  start: number;
  /** End (exclusive); elements without a time range reach to the end of the video. */
  end: number;
}

const FOREVER = Number.MAX_SAFE_INTEGER;

function clipEnd(clip: Pick<OverlayClip, "start" | "sourceIn" | "sourceOut" | "speed">): number {
  return clip.start + Math.max(1, (clip.sourceOut - clip.sourceIn) / (clip.speed || 1));
}

function overlaysOf(timeline: Pick<Timeline, "overlays"> | undefined): OverlayClip[] {
  return timeline?.overlays ?? [];
}

/** Top-level elements of a video (the first page), bottom first. */
function topLevelIds(doc: Pick<SceneDocument, "pages" | "elements">): string[] {
  return (doc.pages[0]?.children ?? []).filter((id) => doc.elements[id]);
}

/** Number of the person track, null without one. */
export function personTrack(doc: Pick<SceneDocument, "timeline">): number | null {
  const index = doc.timeline?.tracks?.findIndex((info) => info.kind === "person") ?? -1;
  return index >= 0 ? index + 1 : null;
}

/**
 * Where the persons of the lowest cut-out track (`split`) are drawn: on the
 * person track (`track`), at least right above their own clips.
 */
export function personLift(
  doc: Pick<SceneDocument, "timeline">,
): { split: number; track: number } | null {
  const split = lowestCutoutTrack(doc.timeline);
  if (split === null) return null;
  const track = personTrack(doc);
  return { split, track: track === null ? split : Math.max(split, track) };
}

/** Whether something on `track` lies behind the cut-out person. */
export function isBehindPersonTrack(doc: Pick<SceneDocument, "timeline">, track: number): boolean {
  const person = personTrack(doc);
  return person !== null && lowestCutoutTrack(doc.timeline) !== null && track < person;
}

/** `cutoutTrack` (video/cutout.ts, which depends on timeline.ts and so on this file). */
function lowestCutoutTrack(timeline: Timeline | undefined): number | null {
  if (!timeline) return null;
  const cut = (clip: { kind: string; cutout?: unknown; device?: unknown }) =>
    clip.kind === "video" && Boolean(clip.cutout) && !clip.device;
  if (timeline.clips.some(cut)) return MAIN_TRACK_NUMBER;
  let lowest: number | null = null;
  for (const clip of overlaysOf(timeline)) {
    if (cut(clip) && (lowest === null || clip.track < lowest)) lowest = clip.track;
  }
  return lowest;
}

/**
 * Older documents: an element marked `behindCutout` lies behind the person
 * on no track of its own (until `normalizeVideoLayers` gives it one).
 */
export function isBehindPerson(
  doc: Pick<SceneDocument, "timeline">,
  element: Pick<SceneElement, "behindCutout" | "type">,
): boolean {
  return (
    Boolean(element.behindCutout) &&
    element.type !== "captions" &&
    lowestCutoutTrack(doc.timeline) !== null &&
    personTrack(doc) === null
  );
}

const rangeOf = (element: Pick<SceneElement, "time">) => ({
  start: element.time?.start ?? 0,
  end: element.time?.end ?? FOREVER,
});

const overlaps = (a: { start: number; end: number }, b: { start: number; end: number }) =>
  a.start < b.end - 0.5 && b.start < a.end - 0.5;

/**
 * The track of every top-level element that lies on one (not behind the
 * person). Elements without `track` go above everything they overlap, in
 * their order on the page (the captions last) – where they were drawn
 * before the tracks existed.
 */
export function resolveElementTracks(doc: SceneDocument): Map<string, number> {
  const out = new Map<string, number>();
  const placed: { track: number; start: number; end: number }[] = overlaysOf(doc.timeline).map(
    (clip) => ({ track: clip.track, start: clip.start, end: clipEnd(clip) }),
  );
  const pending: string[] = [];
  for (const id of topLevelIds(doc)) {
    const element = doc.elements[id]!;
    if (isBehindPerson(doc, element)) continue;
    if (element.track) {
      out.set(id, element.track);
      placed.push({ track: element.track, ...rangeOf(element) });
    } else pending.push(id);
  }
  pending.sort(
    (a, b) =>
      Number(doc.elements[a]!.type === "captions") - Number(doc.elements[b]!.type === "captions"),
  );
  const person = personTrack(doc);
  for (const id of pending) {
    const range = rangeOf(doc.elements[id]!);
    let track = 1;
    for (const item of placed) if (overlaps(item, range)) track = Math.max(track, item.track + 1);
    // Never on the person track: in front of the person.
    if (track === person) track++;
    track = Math.min(MAX_TRACKS, track);
    out.set(id, track);
    placed.push({ track, ...range });
  }
  return out;
}

/** Everything on the tracks above the main track. */
export function trackEntries(doc: SceneDocument): TrackEntry[] {
  const out: TrackEntry[] = overlaysOf(doc.timeline).map((clip) => ({
    kind: "clip",
    id: clip.id,
    track: clip.track,
    start: clip.start,
    end: clipEnd(clip),
  }));
  for (const [id, track] of resolveElementTracks(doc)) {
    out.push({ kind: "element", id, track, ...rangeOf(doc.elements[id]!) });
  }
  return out;
}

/** Number of tracks above the main track (with content or kept). */
export function trackCount(doc: SceneDocument): number {
  let count = doc.timeline?.tracks?.length ?? 0;
  for (const entry of trackEntries(doc)) count = Math.max(count, entry.track);
  return Math.min(MAX_TRACKS, count);
}

/** Description of track `track` (a default one for tracks without an entry). */
export function trackInfo(doc: Pick<SceneDocument, "timeline">, track: number): TrackInfo | null {
  return doc.timeline?.tracks?.[track - 1] ?? null;
}

/** Whether [start, end) is free on `track`, ignoring the entries in `exclude`. */
export function isTrackFree(
  doc: SceneDocument,
  track: number,
  start: number,
  end: number,
  exclude: ReadonlySet<string> = new Set(),
  entries: readonly TrackEntry[] = trackEntries(doc),
): boolean {
  if (track === personTrack(doc)) return false;
  return entries.every(
    (entry) =>
      entry.track !== track || exclude.has(entry.id) || !overlaps(entry, { start, end: end }),
  );
}

// ---------------------------------------------------------------------------
// Changing tracks (mutating, like timeline.ts)
// ---------------------------------------------------------------------------

/** Writes the resolved tracks into the elements and gives every track its entry. */
function materialize(doc: SceneDocument): TrackInfo[] {
  const timeline = doc.timeline!;
  for (const [id, track] of resolveElementTracks(doc)) {
    const element = doc.elements[id]!;
    if (element.track !== track) element.track = track;
  }
  const count = trackCount(doc);
  timeline.tracks ??= [];
  while (timeline.tracks.length < count) timeline.tracks.push({ id: createId("track") });
  return timeline.tracks;
}

/** Renumbers all tracks: `map(old)` gives the new number of an old track. */
function renumber(doc: SceneDocument, map: (track: number) => number) {
  for (const clip of overlaysOf(doc.timeline)) {
    const next = map(clip.track);
    if (next !== clip.track) clip.track = next;
  }
  for (const id of topLevelIds(doc)) {
    const element = doc.elements[id]!;
    if (!element.track) continue;
    const next = map(element.track);
    if (next !== element.track) element.track = next;
  }
}

/**
 * Inserts an empty track at position `at` (1 = directly above the main
 * track); the tracks from there on move up. Kept until removed by hand.
 * Returns the new track's number.
 */
export function insertTrack(doc: SceneDocument, at: number, info: Partial<TrackInfo> = {}): number {
  if (!doc.timeline) return 0;
  const tracks = materialize(doc);
  const position = Math.max(1, Math.min(at, tracks.length + 1, MAX_TRACKS));
  if (tracks.length >= MAX_TRACKS) return position;
  renumber(doc, (track) => (track >= position ? track + 1 : track));
  const group = neighbourGroup(tracks, position);
  const entry: TrackInfo = {
    id: createId("track"),
    keep: true,
    ...(group ? { group } : {}),
    ...info,
  };
  if (!entry.keep) delete entry.keep;
  tracks.splice(position - 1, 0, entry);
  return position;
}

/** The group a track inserted at `position` joins: when the tracks below and above share one. */
function neighbourGroup(tracks: readonly TrackInfo[], position: number): string | undefined {
  const below = tracks[position - 2]?.group;
  const above = tracks[position - 1]?.group;
  return below && below === above ? below : undefined;
}

/** Removes a track with everything on it; the tracks above move down. */
export function removeTrack(doc: SceneDocument, track: number): void {
  if (!doc.timeline) return;
  const tracks = materialize(doc);
  if (track < 1 || track > tracks.length) return;
  doc.timeline.overlays = overlaysOf(doc.timeline).filter((clip) => clip.track !== track);
  for (const id of topLevelIds(doc)) {
    if (doc.elements[id]!.track === track) removeElementTree(doc, id);
  }
  renumber(doc, (value) => (value > track ? value - 1 : value));
  tracks.splice(track - 1, 1);
  pruneGroups(doc);
}

function removeElementTree(doc: SceneDocument, id: string) {
  const element = doc.elements[id];
  if (!element) return;
  if (element.type === "group") for (const child of element.children) removeElementTree(doc, child);
  delete doc.elements[id];
  for (const page of doc.pages) page.children = page.children.filter((child) => child !== id);
}

/**
 * Moves the `count` tracks starting at `first` so that they end up right
 * below the track that is at position `before` now (`trackCount + 1`: on
 * top). Returns the new number of the first moved track.
 */
export function moveTracks(
  doc: SceneDocument,
  first: number,
  count: number,
  before: number,
): number {
  if (!doc.timeline) return first;
  const tracks = materialize(doc);
  const last = first + count - 1;
  if (first < 1 || count < 1 || last > tracks.length) return first;
  if (before >= first && before <= last + 1) return first;
  const order = tracks.map((_, index) => index + 1);
  const block = order.splice(first - 1, count);
  const insertAt = before > last ? before - 1 - count : before - 1;
  order.splice(insertAt, 0, ...block);
  const next = new Map(order.map((track, index) => [track, index + 1]));
  renumber(doc, (track) => next.get(track) ?? track);
  const moved = order.map((track) => tracks[track - 1]!);
  // Moved into a group: the tracks join it; moved out: they leave theirs.
  const start = insertAt + 1;
  const group = neighbourGroup(
    moved.filter((_, index) => index < insertAt || index >= insertAt + count),
    start,
  );
  const blockGroups = new Set(block.map((track) => tracks[track - 1]!.group));
  const wholeGroup =
    blockGroups.size === 1 &&
    [...blockGroups][0] !== undefined &&
    tracks.filter((info) => info.group === [...blockGroups][0]).length === count;
  for (let index = insertAt; index < insertAt + count; index++) {
    const info = clone(moved[index]!);
    if (!wholeGroup) {
      if (group) info.group = group;
      else delete info.group;
    }
    moved[index] = info;
  }
  doc.timeline.tracks = moved;
  pruneGroups(doc);
  return start;
}

/** Hides or shows a track in the preview and the export. */
export function setTrackHidden(doc: SceneDocument, track: number, hidden: boolean): void {
  if (!doc.timeline) return;
  const info = materialize(doc)[track - 1];
  if (!info) return;
  if (hidden) info.hidden = true;
  else delete info.hidden;
}

export function renameTrack(doc: SceneDocument, track: number, name: string): void {
  if (!doc.timeline) return;
  const info = materialize(doc)[track - 1];
  if (!info) return;
  const clean = name.trim().slice(0, 80);
  if (clean) info.name = clean;
  else delete info.name;
}

/**
 * Puts the tracks in one new group. Tracks that are not next to each other
 * move together first (below the highest of them). Returns the group id.
 */
export function groupTracks(
  doc: SceneDocument,
  tracks: readonly number[],
  name?: string,
): string | null {
  if (!doc.timeline) return null;
  const infos = materialize(doc);
  const wanted = [...new Set(tracks)]
    .filter((track) => track >= 1 && track <= infos.length)
    .sort((a, b) => a - b);
  if (wanted.length === 0) return null;
  const ids = wanted.map((track) => infos[track - 1]!.id);
  const position = (id: string) => doc.timeline!.tracks!.findIndex((info) => info.id === id) + 1;
  // Gather them right below the highest one, keeping their order.
  for (let index = ids.length - 2; index >= 0; index--) {
    const current = position(ids[index]!);
    const anchor = position(ids[index + 1]!);
    if (current !== anchor - 1) moveTracks(doc, current, 1, anchor);
  }
  const group: TrackGroup = {
    id: createId("group"),
    name: name?.trim() || nextGroupName(doc.timeline.trackGroups ?? []),
  };
  doc.timeline.trackGroups ??= [];
  doc.timeline.trackGroups.push(group);
  for (const info of doc.timeline.tracks!) {
    if (ids.includes(info.id)) info.group = group.id;
  }
  pruneGroups(doc);
  return group.id;
}

function nextGroupName(groups: readonly TrackGroup[]): string {
  const taken = new Set(groups.map((group) => group.name));
  for (let n = 1; ; n++) if (!taken.has(`Gruppe ${n}`)) return `Gruppe ${n}`;
}

/** Dissolves a group; its tracks stay where they are. */
export function ungroupTracks(doc: SceneDocument, groupId: string): void {
  const timeline = doc.timeline;
  if (!timeline) return;
  for (const info of timeline.tracks ?? []) if (info.group === groupId) delete info.group;
  timeline.trackGroups = (timeline.trackGroups ?? []).filter((group) => group.id !== groupId);
  if (timeline.trackGroups.length === 0) delete timeline.trackGroups;
}

export function updateTrackGroup(
  doc: SceneDocument,
  groupId: string,
  patch: { name?: string; collapsed?: boolean },
): void {
  const group = doc.timeline?.trackGroups?.find((item) => item.id === groupId);
  if (!group) return;
  if (patch.name?.trim()) group.name = patch.name.trim().slice(0, 80);
  if (patch.collapsed !== undefined) {
    if (patch.collapsed) group.collapsed = true;
    else delete group.collapsed;
  }
}

/** Consecutive tracks of a group (bottom first); empty when it has none. */
export function groupTrackNumbers(doc: Pick<SceneDocument, "timeline">, groupId: string): number[] {
  const out: number[] = [];
  (doc.timeline?.tracks ?? []).forEach((info, index) => {
    if (info.group === groupId) out.push(index + 1);
  });
  return out;
}

/** Groups keep consecutive tracks; groups without tracks disappear. */
function pruneGroups(doc: SceneDocument) {
  const timeline = doc.timeline;
  if (!timeline) return;
  const tracks = timeline.tracks ?? [];
  const known = new Set((timeline.trackGroups ?? []).map((group) => group.id));
  const closed = new Set<string>();
  let previous: string | undefined;
  for (const info of tracks) {
    const group = info.group;
    if (group && (!known.has(group) || closed.has(group))) {
      delete info.group;
    } else if (group !== previous && previous) closed.add(previous);
    previous = info.group;
  }
  const used = new Set(tracks.map((info) => info.group).filter(Boolean));
  if (timeline.trackGroups) {
    const kept = timeline.trackGroups.filter((group) => used.has(group.id));
    if (kept.length !== timeline.trackGroups.length) timeline.trackGroups = kept;
    if (timeline.trackGroups.length === 0) delete timeline.trackGroups;
  }
}

/** Moves everything on the given tracks by `delta` ms (not before 0: the earliest item stops there). */
export function shiftTracksInTime(
  doc: SceneDocument,
  tracks: readonly number[],
  delta: number,
): void {
  const set = new Set(tracks);
  const entries = trackEntries(doc).filter((entry) => set.has(entry.track));
  if (entries.length === 0) return;
  const earliest = Math.min(...entries.map((entry) => entry.start));
  const shift = Math.round(Math.max(delta, -earliest));
  if (shift === 0) return;
  for (const clip of overlaysOf(doc.timeline)) {
    if (set.has(clip.track)) clip.start = Math.max(0, clip.start + shift);
  }
  const resolved = resolveElementTracks(doc);
  for (const [id, track] of resolved) {
    const element = doc.elements[id]!;
    if (!set.has(track)) continue;
    if (!element.time) continue;
    const range = element.time;
    element.track = track;
    moveElementTime(doc, id, range.start + shift, range.end + shift);
  }
}

function moveElementTime(doc: SceneDocument, id: string, start: number, end: number, depth = 0) {
  const element = doc.elements[id];
  if (!element || depth > 50) return;
  if (element.time) {
    const delta = start - element.time.start;
    element.time = { start: Math.round(start), end: Math.round(end) };
    if (element.type === "group") {
      for (const child of element.children) {
        const time = doc.elements[child]?.time;
        if (time) moveElementTime(doc, child, time.start + delta, time.end + delta, depth + 1);
      }
    }
  }
}

/**
 * Puts top-level elements on `track`. Where the place is taken there, a new
 * track right above it takes the element (so it still lies above what it
 * was dropped on). Returns the tracks the elements ended up on.
 */
export function moveElementsToTrack(
  doc: SceneDocument,
  ids: readonly string[],
  track: number,
): number[] {
  if (!doc.timeline) return [];
  materialize(doc);
  const moving = new Set(ids);
  const out: number[] = [];
  for (const id of ids) {
    const element = doc.elements[id];
    if (!element || !topLevelIds(doc).includes(id)) continue;
    delete element.behindCutout;
    const range = rangeOf(element);
    let target = Math.max(1, Math.min(MAX_TRACKS, track));
    if (target > trackCount(doc)) target = insertTrack(doc, trackCount(doc) + 1, { keep: false });
    else if (!isTrackFree(doc, target, range.start, range.end, moving)) {
      target = insertTrack(doc, target + 1, { keep: false });
    }
    element.track = target;
    out.push(target);
  }
  return out;
}

/** Moves a clip of an upper track to `start` on `track`; a new track above takes it when the place is taken. */
export function placeOverlayClip(
  doc: SceneDocument,
  clipId: string,
  start: number,
  track: number,
): number {
  const clip = overlaysOf(doc.timeline).find((item) => item.id === clipId);
  if (!clip || !doc.timeline) return 0;
  materialize(doc);
  const length = clipEnd(clip) - clip.start;
  clip.start = Math.max(0, Math.round(start));
  let target = Math.max(1, Math.min(MAX_TRACKS, track));
  if (target > trackCount(doc)) target = insertTrack(doc, trackCount(doc) + 1, { keep: false });
  else if (!isTrackFree(doc, target, clip.start, clip.start + length, new Set([clip.id]))) {
    target = insertTrack(doc, target + 1, { keep: false });
  }
  clip.track = target;
  return target;
}

/**
 * One track up or down for a clip or a top-level element. Where the place
 * there is taken, a new track right past it takes the item, so it really
 * passes what lies there. Returns the new track.
 */
export function stepTrack(doc: SceneDocument, id: string, direction: 1 | -1): number {
  if (!doc.timeline) return 0;
  materialize(doc);
  const clip = overlaysOf(doc.timeline).find((item) => item.id === id);
  const element = clip ? null : doc.elements[id];
  const current = clip?.track ?? element?.track;
  if (!current) return 0;
  const range = clip ? { start: clip.start, end: clipEnd(clip) } : rangeOf(element!);
  const set = (track: number) => {
    if (clip) clip.track = track;
    else element!.track = track;
  };
  const target = current + direction;
  const count = trackCount(doc);
  if (target < 1 || target > count) {
    // Already at the bottom / on top by itself: nothing to pass.
    if (isTrackFree(doc, current, 0, FOREVER, new Set([id]))) return current;
    set(insertTrack(doc, direction > 0 ? count + 1 : 1, { keep: false }));
  } else if (isTrackFree(doc, target, range.start, range.end, new Set([id]))) {
    set(target);
  } else {
    set(insertTrack(doc, direction > 0 ? target + 1 : target, { keep: false }));
  }
  compactTracks(doc);
  return clip?.track ?? element?.track ?? current;
}

/**
 * Track for a new clip of an upper track at [start, end): above every clip
 * it overlaps, but below the titles and graphics there (a new track goes in
 * below them when needed).
 */
export function trackForNewClip(doc: SceneDocument, start: number, end: number): number {
  if (!doc.timeline) return 1;
  const entries = trackEntries(doc);
  let track = 1;
  for (const entry of entries) {
    if (entry.kind === "clip" && overlaps(entry, { start, end })) {
      track = Math.max(track, entry.track + 1);
    }
  }
  if (track <= trackCount(doc) && isTrackFree(doc, track, start, end, new Set(), entries))
    return track;
  return insertTrack(doc, track, { keep: false });
}

/**
 * Tracks without content disappear (unless kept, named, hidden or grouped);
 * the order of the others stays. Elements and clips are renumbered.
 */
export function compactTracks(doc: SceneDocument): void {
  if (!doc.timeline) return;
  const tracks = materialize(doc);
  const used = new Set(trackEntries(doc).map((entry) => entry.track));
  const next = new Map<number, number>();
  const kept: TrackInfo[] = [];
  tracks.forEach((info, index) => {
    const track = index + 1;
    const keep =
      used.has(track) || info.keep || info.name || info.hidden || info.group || info.kind;
    if (!keep) return;
    kept.push(info);
    next.set(track, kept.length);
  });
  if (kept.length === tracks.length) return;
  renumber(doc, (track) => next.get(track) ?? track);
  doc.timeline.tracks = kept;
  pruneGroups(doc);
}

/**
 * Keeps a video document consistent after every change: top-level elements
 * get a track (children of groups none, elements behind the person none),
 * every track its entry, the page's element order follows the tracks (the
 * canvas and the layer list show that order). With `compact`, tracks that
 * became empty go away (not during a drag: the rows would jump).
 */
export function normalizeVideoLayers(
  doc: SceneDocument,
  options: { compact?: boolean } = {},
): void {
  if (doc.kind !== "video" || !doc.timeline) return;
  const top = new Set(topLevelIds(doc));
  for (const element of Object.values(doc.elements)) {
    if (element.track === undefined) continue;
    if (!top.has(element.id) || isBehindPerson(doc, element)) delete element.track;
  }
  for (const clip of overlaysOf(doc.timeline)) {
    if (clip.track > MAX_TRACKS) clip.track = MAX_TRACKS;
  }
  normalizePersonTrack(doc);
  materialize(doc);
  if (options.compact) compactTracks(doc);
  if (doc.timeline.tracks!.length === 0) delete doc.timeline.tracks;
  pruneGroups(doc);
  sortPageByStack(doc);
}

/**
 * One person track while a person is cut out, none without: a new one goes
 * right above the lowest cut-out track. Elements of older documents marked
 * `behindCutout` move onto a track right below it.
 */
function normalizePersonTrack(doc: SceneDocument) {
  const timeline = doc.timeline!;
  const split = lowestCutoutTrack(timeline);
  const persons = (timeline.tracks ?? []).flatMap((info, index) =>
    info.kind === "person" ? [index + 1] : [],
  );
  const keep = split === null ? [] : persons.slice(0, 1);
  for (const track of persons.filter((item) => !keep.includes(item)).reverse()) {
    removeTrack(doc, track);
  }
  if (split !== null && keep.length === 0) {
    const legacy = topLevelIds(doc).filter((id) => isBehindPerson(doc, doc.elements[id]!));
    let at = split + 1;
    if (legacy.length > 0) {
      const tracks = moveElementsToTrack(doc, legacy, insertTrack(doc, at, { keep: false }));
      at = Math.max(...tracks) + 1;
    }
    insertTrack(doc, at, { kind: "person" });
  }
  for (const element of Object.values(doc.elements)) {
    if (element.behindCutout && personTrack(doc) !== null) delete element.behindCutout;
  }
}

/**
 * Puts clips of upper tracks and top-level elements behind the cut-out
 * person (onto the track right below the person track, a new one when the
 * place is taken) or in front of it (right above).
 */
export function placeBehindPerson(doc: SceneDocument, ids: readonly string[], behind: boolean) {
  if (!doc.timeline) return;
  normalizePersonTrack(doc);
  materialize(doc);
  for (const id of ids) {
    const person = personTrack(doc);
    if (person === null) return;
    const clip = overlaysOf(doc.timeline).find((item) => item.id === id);
    const current = clip?.track ?? doc.elements[id]?.track;
    if (current === undefined || doc.elements[id]?.type === "captions") continue;
    if (behind === current < person) continue;
    // Behind: onto the track below the person (taken: a new one between them).
    const target = behind ? Math.max(1, person - 1) : person + 1;
    if (clip) {
      if (behind && person === 1)
        placeOverlayClip(doc, id, clip.start, insertTrack(doc, 1, { keep: false }));
      else placeOverlayClip(doc, id, clip.start, target);
    } else if (behind && person === 1) {
      moveElementsToTrack(doc, [id], insertTrack(doc, 1, { keep: false }));
    } else moveElementsToTrack(doc, [id], target);
  }
  compactTracks(doc);
}

/** The first page's elements in drawing order (stable for elements with the same place). */
function sortPageByStack(doc: SceneDocument) {
  const page = doc.pages[0];
  if (!page) return;
  const split = personTrack(doc) === null ? lowestCutoutTrack(doc.timeline) : null;
  const tracks = resolveElementTracks(doc);
  const keyOf = (id: string) => elementStackKey(doc.elements[id], tracks.get(id), split);
  const order = page.children.map((id, index) => ({ id, index, key: keyOf(id) }));
  order.sort((a, b) => a.key - b.key || a.index - b.index);
  if (order.some((item, index) => item.id !== page.children[index])) {
    page.children = order.map((item) => item.id);
  }
}

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

/** Numbers of the hidden tracks. */
export function hiddenTracks(doc: Pick<SceneDocument, "timeline">): Set<number> {
  const out = new Set<number>();
  (doc.timeline?.tracks ?? []).forEach((info, index) => {
    if (info.hidden) out.add(index + 1);
  });
  return out;
}

/** Top-level elements on hidden tracks. */
export function elementsOnHiddenTracks(doc: SceneDocument): Set<string> {
  const hidden = hiddenTracks(doc);
  const out = new Set<string>();
  if (hidden.size === 0) return out;
  for (const [id, track] of resolveElementTracks(doc)) if (hidden.has(track)) out.add(id);
  return out;
}

/** A normalized copy (the export: older documents drawn like the editor shows them now). */
export function normalizedVideoCopy(doc: SceneDocument): SceneDocument {
  if (doc.kind !== "video" || !doc.timeline) return doc;
  const out = clone(doc);
  normalizeVideoLayers(out, { compact: true });
  return out;
}

/** The document as the export shows it: without what lies on hidden tracks. */
export function withoutHiddenTracks(doc: SceneDocument): SceneDocument {
  const hidden = hiddenTracks(doc);
  if (hidden.size === 0) return doc;
  const out = clone(doc);
  for (const id of elementsOnHiddenTracks(doc)) out.elements[id]!.hidden = true;
  if (out.timeline?.overlays) {
    out.timeline.overlays = out.timeline.overlays.filter((clip) => !hidden.has(clip.track));
  }
  return out;
}

// ---------------------------------------------------------------------------
// Drawing order (editor preview and export)
// ---------------------------------------------------------------------------

/**
 * Place of something in the drawing order: per track the pictures, then the
 * elements behind the person (older documents only), the cut-out persons,
 * then the elements of the track. The persons of the lowest cut-out track
 * are drawn on the person track (`personLift`).
 */
export const STACK_PART = { picture: 0, behind: 1, person: 2, element: 3 } as const;

export function stackKey(track: number, part: keyof typeof STACK_PART): number {
  return track * 4 + STACK_PART[part];
}

function elementStackKey(
  element: SceneElement | undefined,
  track: number | undefined,
  split: number | null,
): number {
  if (!element) return Number.MAX_SAFE_INTEGER;
  if (split !== null && element.behindCutout && element.type !== "captions") {
    return stackKey(split, "behind");
  }
  return stackKey(track ?? MAX_TRACKS + 1, "element");
}

/**
 * The elements in bands between the videos: band `i` is drawn after video
 * slot `i` and before slot `i + 1` (slot 0: the main track and everything
 * below the first band). Elements of one band are drawn by one renderer
 * layer, the clips of one slot on one canvas (preview) or in one run of
 * the final pass (export).
 */
export interface VideoStack {
  bands: { key: number; ids: string[] }[];
}

export function videoStack(doc: SceneDocument): VideoStack {
  const split = personTrack(doc) === null ? lowestCutoutTrack(doc.timeline) : null;
  const tracks = resolveElementTracks(doc);
  const elements = topLevelIds(doc)
    .map((id, index) => ({
      id,
      index,
      key: elementStackKey(doc.elements[id], tracks.get(id), split),
    }))
    .sort((a, b) => a.key - b.key || a.index - b.index);
  const videoKeys = new Set<number>([stackKey(MAIN_TRACK_NUMBER, "picture")]);
  const lift = personLift(doc);
  const personKey = (track: number) =>
    stackKey(lift && track === lift.split ? lift.track : track, "person");
  const cut = (clip: { kind: string; cutout?: { background: string }; device?: unknown }) =>
    clip.kind === "video" && clip.cutout && !clip.device ? clip.cutout : null;
  if (doc.timeline?.clips.some((clip) => cut(clip))) {
    videoKeys.add(personKey(MAIN_TRACK_NUMBER));
  }
  for (const clip of overlaysOf(doc.timeline)) {
    const cutout = cut(clip);
    if (cutout?.background !== "remove") videoKeys.add(stackKey(clip.track, "picture"));
    if (cutout) videoKeys.add(personKey(clip.track));
  }
  const sortedVideo = [...videoKeys].sort((a, b) => a - b);
  const bands: VideoStack["bands"] = [];
  let previous = -1;
  for (const item of elements) {
    const band = bands[bands.length - 1];
    const between = sortedVideo.some((key) => key > previous && key < item.key);
    if (!band || between) bands.push({ key: item.key, ids: [item.id] });
    else band.ids.push(item.id);
    previous = item.key;
  }
  return { bands };
}

/** The video slot something with the drawing key `key` belongs to. */
export function stackSlot(stack: VideoStack, key: number): number {
  let slot = 0;
  for (const band of stack.bands) if (band.key < key) slot++;
  return slot;
}

// ---------------------------------------------------------------------------
// "Nach vorne" / "Nach hinten" in videos: along the tracks
// ---------------------------------------------------------------------------

/**
 * Moves elements up or down the stack of what they overlap in time: one
 * step (forward/backward) or all the way (front/back). The element goes onto
 * a new track right past the item it passes, so nothing else moves.
 */
export function reorderVideoElements(
  doc: SceneDocument,
  ids: readonly string[],
  mode: "forward" | "backward" | "front" | "back",
): void {
  if (!doc.timeline) return;
  materialize(doc);
  const order = mode === "forward" || mode === "front" ? [...ids].reverse() : [...ids];
  for (const id of order) {
    const element = doc.elements[id];
    if (!element?.track) continue;
    const range = rangeOf(element);
    const own = element.track;
    const others = trackEntries(doc).filter(
      (entry) => entry.id !== id && !ids.includes(entry.id) && overlaps(entry, range),
    );
    const up = mode === "forward" || mode === "front";
    const candidates = others
      .map((entry) => entry.track)
      .filter((track) => (up ? track > own : track < own))
      .sort((a, b) => (up ? a - b : b - a));
    if (candidates.length === 0) continue;
    const past =
      mode === "forward" || mode === "backward"
        ? candidates[0]!
        : candidates[candidates.length - 1]!;
    if (up) {
      const target = past + 1;
      if (
        target <= trackCount(doc) &&
        isTrackFree(doc, target, range.start, range.end, new Set([id]))
      ) {
        element.track = target;
      } else element.track = insertTrack(doc, target, { keep: false });
    } else {
      const target = past - 1;
      if (target >= 1 && isTrackFree(doc, target, range.start, range.end, new Set([id]))) {
        element.track = target;
      } else {
        // A new track right below the one it passes.
        element.track = insertTrack(doc, past, { keep: false });
      }
    }
  }
  compactTracks(doc);
}
